import { FC, useState, useEffect } from "react";
import { Modal, Form, Row, Col, Button } from "react-bootstrap";
import { Budget, BudgetType, ReplenishFrequency } from "@models/budget.model";
import { CreateBudgetPayload } from "@services/finance/budgets/commands/cmdCreateBudget";
import { UpdateBudgetPayload } from "@services/finance/budgets/commands/cmdUpdateBudget";
import {
  BILLING_MODEL_OPTIONS,
  REPLENISH_FREQUENCY_LABELS,
} from "../../types";

interface Props {
  show: boolean;
  onClose: () => void;
  onSave: (payload: CreateBudgetPayload | UpdateBudgetPayload) => void;
  budget: Budget | null;
  contractId: number;
  /** The contract's billing method. When set, Budget Type is locked to mirror it. */
  contractBillingMethod?: string | null;
  isSaving: boolean;
}

interface FormState {
  budget_type: BudgetType;
  starting_balance: string;
  replenish_amount: string;
  replenish_frequency: ReplenishFrequency | "";
  start_date: string;
  end_date: string;
  is_active: boolean;
}

const EMPTY: FormState = {
  budget_type: "FLAT_FEE",
  starting_balance: "",
  replenish_amount: "",
  replenish_frequency: "",
  start_date: "",
  end_date: "",
  is_active: true,
};

const BUDGET_VALUES = new Set(BILLING_MODEL_OPTIONS.map((o) => o.value));
const FREQUENCIES: ReplenishFrequency[] = [
  "DAILY",
  "WEEKLY",
  "MONTHLY",
  "QUARTERLY",
  "YEARLY",
];

const BudgetFormModal: FC<Props> = ({
  show,
  onClose,
  onSave,
  budget,
  contractId,
  contractBillingMethod,
  isSaving,
}) => {
  const isEdit = budget !== null;

  // Budget Type mirrors the contract's billing method (locked) when the contract
  // has one that's part of the shared list.
  const lockedType =
    contractBillingMethod && BUDGET_VALUES.has(contractBillingMethod)
      ? (contractBillingMethod as BudgetType)
      : null;
  const isLocked = lockedType !== null;

  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (!show) return;
    if (budget) {
      setForm({
        budget_type: lockedType ?? budget.budget_type,
        starting_balance: String(budget.starting_balance ?? ""),
        replenish_amount: budget.replenish_amount != null ? String(budget.replenish_amount) : "",
        replenish_frequency: budget.replenish_frequency ?? "",
        start_date: budget.start_date ?? "",
        end_date: budget.end_date ?? "",
        is_active: budget.is_active,
      });
    } else {
      setForm({ ...EMPTY, budget_type: lockedType ?? EMPTY.budget_type });
    }
  }, [budget, show, contractBillingMethod]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const isSubscription = form.budget_type === "SUBSCRIPTION";

  const handleSave = () => {
    const startingBalance = parseFloat(form.starting_balance);
    const common = {
      budget_type: form.budget_type,
      starting_balance: isNaN(startingBalance) ? 0 : startingBalance,
      replenish_amount: isSubscription && form.replenish_amount
        ? parseFloat(form.replenish_amount)
        : 0,
      replenish_frequency:
        isSubscription && form.replenish_frequency ? form.replenish_frequency : null,
      start_date: form.start_date,
      end_date: form.end_date || null,
      is_active: form.is_active,
    };

    if (isEdit) {
      onSave(common satisfies UpdateBudgetPayload);
    } else {
      onSave({ ...common, contract_id: contractId } satisfies CreateBudgetPayload);
    }
  };

  const canSubmit =
    !!form.start_date &&
    form.starting_balance !== "" &&
    (!isSubscription || (!!form.replenish_amount && !!form.replenish_frequency));

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">{isEdit ? "Edit Budget" : "Add Budget"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              Budget Type <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              value={form.budget_type}
              disabled={isLocked}
              onChange={(e) => update("budget_type", e.target.value as BudgetType)}
            >
              {BILLING_MODEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              {isLocked
                ? "Locked to the contract's billing method."
                : form.budget_type === "FLAT_FEE"
                ? "Credits the spend account once at start date."
                : form.budget_type === "SUBSCRIPTION"
                ? "Credits the spend account on a recurring schedule."
                : ""}
            </Form.Text>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  Budget Amount <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.starting_balance}
                  onChange={(e) => update("starting_balance", e.target.value)}
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  Start Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={form.start_date}
                  onChange={(e) => update("start_date", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          {isSubscription && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    Replenish Amount <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.replenish_amount}
                    onChange={(e) => update("replenish_amount", e.target.value)}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    Replenish Frequency <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={form.replenish_frequency}
                    onChange={(e) =>
                      update("replenish_frequency", e.target.value as ReplenishFrequency | "")
                    }
                  >
                    <option value="">Select...</option>
                    {FREQUENCIES.map((f) => (
                      <option key={f} value={f}>
                        {REPLENISH_FREQUENCY_LABELS[f]}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          )}

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">End Date</Form.Label>
            <Form.Control
              type="date"
              value={form.end_date}
              onChange={(e) => update("end_date", e.target.value)}
            />
            <Form.Text className="text-muted">Leave blank for an ongoing budget.</Form.Text>
          </Form.Group>

          <Form.Check
            type="switch"
            id="budget-active-switch"
            label="Active"
            checked={form.is_active}
            onChange={(e) => update("is_active", e.target.checked)}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSaving || !canSubmit}>
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" /> Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-circle me-2" />
              {isEdit ? "Save Changes" : "Add Budget"}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BudgetFormModal;
